import React from 'react';
import { FaMicrophone, FaMicrophoneSlash } from 'react-icons/fa6';
import { DEFAULT_AVATAR } from '../../../../shared/utils/matrix/matrixRoomUtils';
import CreatorCreditsNotifyModal from './component/CreatorCreditsNotifyModal';
import {
  formatHandle,
  getCallReconnectBannerLabel,
  getMatrixSdkCallStateLabel,
} from './utils/callFormatUtils';
import { CALL_MEDIA_TYPE } from './constant';

const VoiceCallUI = ({
  isCreator,
  minimumCallTimeMinutes,
  pricingModel,
  totalTipCollectedAmount,
  tipText,
  opponentDisplayName,
  opponentAvatar,
  callDuration,
  micMuted,
  sdkCallState,
  iceConnectionState,
  isOnline,
  onToggleMic,
  onEndCall,
  onOpenCredits,
  onOpenTip,
  tipDisabled = false,
  showCreditsNotify,
  dismissCreditsNotify,
  creatorLowBalanceMinutesRemaining = null,
}) => {
  const opponentHandle = formatHandle(opponentDisplayName);

  const callStateLabel = getMatrixSdkCallStateLabel(sdkCallState);

  const reconnectLabel = getCallReconnectBannerLabel({
    isOnline,
    iceConnectionState,
    sdkCallState,
    variant: CALL_MEDIA_TYPE.VOICE,
  });

  const stateToneClass =
    sdkCallState === 'connected' ? 'cm_call_state_connected' : 'cm_call_state_pending';

  const minCallTimeLabel = Number.isFinite(Number(minimumCallTimeMinutes))
    ? Math.max(1, Math.round(Number(minimumCallTimeMinutes)))
    : 1;

  const shouldShowDisclaimer = pricingModel === 'flat_rate';
  const disclaimer = shouldShowDisclaimer
    ? isCreator
      ? `A minimum call time of ${minCallTimeLabel} minutes is set. If you end the call early, you'll be paid per second based on your set rate.`
      : 'This creator has a minimum call time set. If you end the call early you will still be charged the full minimum rate.'
    : '';

  const variant = isCreator ? 'creator' : 'subscriber';

  return (
    <div className={`call-wrapper cm_call_ui cm_call_ui_voice cm_call_ui_voice-${variant} ${showCreditsNotify ? 'cm_call_ui_voice_with_notify' : ''}`}>
      <CreatorCreditsNotifyModal
        open={showCreditsNotify}
        onDismiss={dismissCreditsNotify}
        minutesRemaining={creatorLowBalanceMinutesRemaining}
      />
      {tipText ? (
        <div className="cm_call_tip_chip_voice cm_call_voice_tip_banner" role="status">
          {isCreator ? (
            <>
              {tipText}{' '}
              <span role="img" aria-label="celebration">
                🎉
              </span>
            </>
          ) : (
            <>
              {tipText}
              <i className="fa-solid fa-coins cm_call_voice_tip_banner_icon" aria-hidden />
            </>
          )}
        </div>
      ) : null}

      <div className="cm_call_stage cm_call_voice_stage">
        <div className="cm_call_voice_panel">
          <div className="cm_call_voice_timer_badge">
            {callDuration}
          </div>

          {(callStateLabel || reconnectLabel) && (
            <div className={`cm_call_voice_state ${stateToneClass} text-muted`}>
              {reconnectLabel || callStateLabel}
            </div>
          )}

          {disclaimer ? <p className="cm_call_voice_note">{disclaimer}</p> : null}

          <div className="cm_call_voice_profile">
            <img
              src={opponentAvatar || DEFAULT_AVATAR}
              className="cm_call_voice_avatar"
              alt={opponentHandle}
            />
            <h3 className="cm_call_voice_name">{opponentHandle}</h3>
            {/* <div className="cm_call_voice_timer">
              <i className="fa-regular fa-clock" aria-hidden />
              <span>{callDuration}</span>
            </div> */}
          </div>

          {isCreator ? (
            <div className="cm_call_voice_collected" role="status">
              Total collected: <span>{totalTipCollectedAmount}</span>{' '}
              <span role="img" aria-label="money bag">
                💰
              </span>
            </div>
          ) : null}

          {!isCreator ? (
            <div className="cm_call_voice_subscriber_actions">
              <button
                type="button"
                className="cm_call_voice_action_btn"
                onClick={() => onOpenCredits?.()}
                aria-label="Credits"
              >
                <span className="cm_call_voice_action_icon">
                  <i className="fa-solid fa-coins" aria-hidden />
                </span>
                <span className="cm_call_voice_action_label">Credits</span>
              </button>
              <button
                type="button"
                className={`cm_call_voice_action_btn${tipDisabled ? ' cm_call_voice_action_btn_disabled' : ''}`}
                onClick={() => !tipDisabled && onOpenTip?.()}
                disabled={tipDisabled}
                aria-label="Tip"
                aria-disabled={tipDisabled}
              >
                <span className="cm_call_voice_action_icon">
                  <i className="fa-solid fa-dollar-sign" aria-hidden />
                </span>
                <span className="cm_call_voice_action_label">Tip</span>
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="call_actions_btn cm_call_controls cm_call_controls_voice">
        <button
          type="button"
          onClick={onToggleMic}
          className={`cm_call_voice_toggle_btn${micMuted ? ' cm_call_control_active' : ''}`}
          aria-label={micMuted ? 'Unmute mic' : 'Mute mic'}
        >
          {micMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
        </button>

        <button
          type="button"
          className="red_call cm_call_voice_end"
          onClick={onEndCall}
          aria-label="End call"
        >
          <i className="fa-solid fa-phone cm_call_voice_end_phone" aria-hidden />
        </button>
      </div>
    </div>
  );
};

export default VoiceCallUI;
